import classNames from "classnames";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { User } from "../../models/Entities";
import { Briefcase } from "lucide-react";

export enum UserAvatarSize {
  xxl = "avatar-xxl",
  xl = "avatar-xl",
  normal = "",
  sm = "avatar-sm",
  xs = "avatar-xs",
}

interface UserAvatarProps {
  user?: User;
  email?: string;
  size?: UserAvatarSize;
  className?: string;
}

export const UserAvatar = ({
  user,
  email,
  size = UserAvatarSize.normal,
  className = "",
}: UserAvatarProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [avatarLetters, setAvatarLetters] = useState("");

  useEffect(() => {
    setWidth(containerRef.current?.offsetWidth ?? 0);
    setHeight(containerRef.current?.offsetHeight ?? 0);
  }, [containerRef]);

  useMemo(() => {
    if (user) {
      if (user.name) {
        const splitName = user.name.split(" ");
        if (splitName.length > 0) {
          setAvatarLetters(splitName[0].charAt(0) + splitName[splitName.length - 1].charAt(0));
        }
      } else if (user.email) {
        setAvatarLetters(user.email.charAt(0).toUpperCase());
      }
    } else if (email) {
      setAvatarLetters(email.charAt(0).toUpperCase());
    }
  }, [user, email]);

  return user?.avatar ? (
    <div className={"avatar " + size + " " + className}>
      <img src={user?.avatar} className="avatar-img rounded-circle" />
    </div>
  ) : (
    <div
      className={"avatar flex items-center justify-center " + size + " " + className}
      ref={containerRef}
    >
      <span
        className="text-black font-semibold	rounded-circle align-middle"
        style={{ fontSize: Math.min(width, height) * 0.7 }}
      >
        {avatarLetters}
      </span>
    </div>
  );
};

interface OrganizationAvatarProps {
  imageURL?: string | undefined;
  organizationName?: string | undefined;
  size?: UserAvatarSize;
  className?: string;
  isCircle?: boolean;
}

export const OrganizationAvatar = ({
  imageURL,
  organizationName,
  size = UserAvatarSize.normal,
  className = "",
  isCircle,
}: OrganizationAvatarProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    setWidth(containerRef.current?.offsetWidth ?? 0);
    setHeight(containerRef.current?.offsetHeight ?? 0);
  }, [containerRef]);

  return imageURL ? (
    <div className={"flex flex-shrink-0 avatar " + size + " " + className}>
      <img
        src={imageURL}
        className={classNames("bg-white", isCircle ? "rounded-circle" : "deprecated-rounded")}
      />
    </div>
  ) : (
    <div
      className={"flex flex-shrink-0 items-center justify-center avatar " + size + " " + className}
      ref={containerRef}
    >
      <span
        className={classNames(
          "text-black font-semibold	align-middle",
          isCircle ? "rounded-circle" : "deprecated-rounded",
        )}
        style={{ fontSize: Math.min(width, height) * 0.7 }}
      >
        {organizationName ? organizationName.charAt(0).toUpperCase() : <Briefcase />}
      </span>
    </div>
  );
};
